'use client'

import { useRef, useState } from 'react'
import { Play } from 'lucide-react'
import posthog from '@/lib/analytics/posthog-lazy'

import type { LandingMessages } from '@/lib/marketing/landing-messages'

/**
 * The 90-second tour, click to play (it has voice-over, so never autoplay),
 * with the same PostHog events as the classic `DemoVideoSection`
 * (`landing_demo_video_play`, `_error`, `_completed`). es and fr have their
 * own cut; everything else gets the English master.
 */
export function DemoVideo({
  copy,
  locale,
}: {
  copy: LandingMessages['demoVideo']
  locale: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState(false)
  const suffix = locale === 'es' || locale === 'fr' ? `.${locale}` : ''
  const videoLocale = suffix ? locale : 'en'

  const onError = () => {
    setPlaying(false)
    setFailed(true)
    posthog.capture('landing_demo_video_error', { video_locale: videoLocale })
  }

  const play = () => {
    setPlaying(true)
    setFailed(false)
    videoRef.current?.play().catch(onError)
    posthog.capture('landing_demo_video_play', { video_locale: videoLocale })
  }

  return (
    <div className="relative overflow-hidden rounded-en-lg bg-en-ink shadow-en-overlay">
      <video
        ref={videoRef}
        className="block aspect-video w-full"
        src={`/promo/alumnia-demo-video${suffix}.mp4`}
        poster={`/promo/alumnia-demo-poster${suffix}.jpg`}
        preload="metadata"
        playsInline
        controls={playing}
        onEnded={() => posthog.capture('landing_demo_video_completed')}
        onError={onError}
      />
      {playing ? null : (
        <button
          type="button"
          onClick={play}
          aria-label={copy.playAria}
          className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 bg-en-scrim focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-solid focus-visible:outline-en-bg"
        >
          <span className="flex size-16 items-center justify-center rounded-en-full bg-en-bg text-en-ink shadow-en-overlay transition-transform duration-(--en-dur-feedback) group-hover:scale-105 motion-reduce:transition-none">
            <Play aria-hidden="true" className="ml-1 size-7" fill="currentColor" />
          </span>
          <span className="rounded-en-full bg-en-ink px-4 py-1 text-en-label font-semibold text-en-primary-ink">
            {failed ? copy.playError : copy.playHint}
          </span>
        </button>
      )}
    </div>
  )
}
